import { CardContent, Grid } from "@mui/material";
import { AppGlobalClassKeys } from "app/appGlobalStyles";
import { MprcCard, MprcConfirmDialog, MprcRequestErrorReport, MprcRequestIndicator, MprcSpinnerIndicator } from "components";
import { ButtonAdd } from "components/buttons";
import { MprcFilterLine } from "components/filter";
import { MprcCardFilterBox } from "components/filter/MprcCardFilterBox";
import { MprcFilterCount } from "components/filter/MprcFilterCount";
import { IconButtonDelete } from "components/iconButtons";
import { MprcBarTitle } from "components/MprcBarTitle";
import { MprcContainer } from "components/MprcContainer";
import { MprcListItemTitle } from "components/MprcListItemTitle";
import { MprcRequestSnackbarErrorReport } from "components/MprcRequestErrorReport";
import { UnitTypeResponse } from "generated/CoreApi_v1";
import { useSnackbar } from "notistack";
import { useCoreApi } from "providers/CoreApiProvider";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useGobackOrLms } from "views/common/hooks/useGobackOrHome";
import { EditSettingsHeader } from "views/foundation/settings/components/EditSettingsHeader";
import { EditUnitType } from "views/foundation/settings/Home/UnitTypes/EditUnitType";

export const ListUnitTypes: React.FC = () => {

    ////////////////////////////////////////////////////////////////////////////////
    const api = useCoreApi();
    const { t } = useTranslation();
    const gobackOrHome = useGobackOrLms();
    const { enqueueSnackbar } = useSnackbar();

    const [openForm, setOpenForm] = useState<string | undefined | null>(null);
    const [viewData, setViewData] = useState<UnitTypeResponse[]>([]);
    const [viewError, setViewError] = useState<any>(null!);
    const [loading, setLoading] = useState<boolean>(true);
    const [deletingId, setDeleteId] = useState<string | null>(null);
    const [dialog, setDialog] = useState<any>();


    ////////////////////////////////////////////////////////////////////////////////

    const handleAdd = () => {
        setOpenForm(undefined);
    };

    ////////////////////////////////////////////////////////////////////////////////
    /// EFFECT's
    ////////////////////////////////////////////////////////////////////////////////

    // Ao remover um tipo de unidade administrativa
    useEffect(() => {
        async function postData() {
            try {
                if (deletingId) {
                    var result = await api.v1.unitTypeManagement.deleteUnitType({ unitTypeId: deletingId! });
                    if (result.data) {
                        enqueueSnackbar(t("Settings.UnitTypes.List.DeletedSuccessMessage"), { variant: "success" });
                        setLoading(true);
                    }
                    else {
                        enqueueSnackbar(t("Settings.UnitTypes.List.DeletedErrorMessage"), { variant: "error" });
                    }
                }
            } catch (e) {
                MprcRequestSnackbarErrorReport(e);
            }
            setDeleteId(null);
        }

        postData();

    }, [deletingId]);

    /// Ao carregar a lista de papéis:
    useEffect(() => {
        async function getData() {
            try {
                var result = await api.v1.unitType.getUnitTypes({});
                setViewData(result.data);
            }
            catch (e) {
                setViewError(e);
            }
            finally {
                setLoading(false);
            }
        }

        if (loading) getData();

    }, [loading]);

    const handleDelete = (item: UnitTypeResponse) => {
        if (item.hasUnits) {
            enqueueSnackbar(t("Settings.UnitTypes.List.CannotDeletedHasUnitMessage"), { variant: "error" });
        }
        else {
            setDialog(<MprcConfirmDialog
                open
                severity="danger"
                message={t("Settings.UnitTypes.List.ConfirmDeleteMessage")}
                onClose={(yes) => {
                    if (yes) {
                        setDeleteId(item.unitTypeId);
                    }
                    setDialog(null);
                }
                }
            />
            );
        }
    };

    ////////////////////////////////////////////////////////////////////////////////
    /// RENDER
    ////////////////////////////////////////////////////////////////////////////////

    ///
    const renderGrid = () => (
        <Grid container justifyContent="center" alignItems="center" spacing={2}>
            {
                viewData.map((item) => {
                    return (
                        <Grid key={item.unitTypeId} width="100%">
                            <Grid container justifyContent="flex-start" alignItems="flex-start" wrap="nowrap">
                                <Grid size="grow" overflow="hidden">
                                    <Grid container alignItems="center" justifyContent="space-between" flexWrap="nowrap">
                                        <Grid overflow="hidden">
                                            <MprcListItemTitle onClick={() => setOpenForm(item.unitTypeId)} >
                                                {item.name}
                                            </MprcListItemTitle>
                                        </Grid>
                                        <Grid>
                                            <IconButtonDelete size="small" onClick={() => handleDelete(item)} />
                                        </Grid>
                                    </Grid>
                                </Grid>
                            </Grid>
                        </Grid>
                    );
                })
            }
        </Grid >
    );

    ///
    const getHeaderItems = () => {
        const formHeaderOptions: React.ReactNode[] = [];
        formHeaderOptions.push(<MprcBarTitle>{t("Settings.UnitTypes.ViewTitle")}</MprcBarTitle>);
        return formHeaderOptions;
    };

    ///
    const renderContent = () => {
        return (
            <Grid container direction="column" height="100%" spacing={2}>
                <Grid width="100%">
                    <EditSettingsHeader
                        recordChanged={false}
                        actions={getHeaderItems()}
                        onBack={() => gobackOrHome()}
                    />
                </Grid>
                <Grid size="grow" overflow="hidden" width="100%">
                    <Grid container direction="column" height="100%" width="100%">
                        <Grid size="grow" overflow="auto" className={AppGlobalClassKeys.thinScrollbar} width="100%">

                            <MprcContainer>
                                <MprcCard>
                                    <MprcCardFilterBox>
                                        <MprcFilterLine>
                                            <ButtonAdd size="large" color="inherit" onClick={handleAdd} text={t("Settings.UnitTypes.List.AddButtonText")} />
                                        </MprcFilterLine>
                                    </MprcCardFilterBox>
                                    <CardContent>
                                        {
                                            loading
                                                ? <MprcSpinnerIndicator />
                                                : <>
                                                    <MprcContainer>
                                                        <MprcFilterCount
                                                            loading={loading}
                                                            isEmpty={viewData.length == 0}
                                                            countText={t("Settings.UnitTypes.List.RecordsFoundText", { count: viewData.length })}
                                                        />
                                                    </MprcContainer>
                                                    {renderGrid()}
                                                </>
                                        }
                                    </CardContent>
                                </MprcCard>
                            </MprcContainer>

                        </Grid>
                    </Grid>
                </Grid>
            </Grid>
        );
    };

    return (
        <>
            {dialog}

            <MprcRequestErrorReport
                error={viewError}
            />

            <MprcRequestIndicator
                visible={deletingId != null}
                type={"post"}
            />

            {
                openForm === null
                    ? renderContent()
                    : (
                        <EditUnitType
                            unitTypeId={openForm ?? undefined}
                            onClose={
                                (reload) => {
                                    setLoading(reload == true);
                                    setOpenForm(null);
                                }
                            }
                        />
                    )
            }
        </>
    );
};
